import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';
import { ApiResponse } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { appActions } from '../../../../../base/redux/slices/appSlice';

import { timestampWithNoMillisecondsToDate } from '../../../../../base/utils';
import { __t } from '../../../../../base/utils/t';
import { vacancyApi } from '../../api';
import { dispatchVacancyClosed, dispatchVacancyPublished } from '../../utils/events';
import {
    GetVacancyListParams,
    UpgradeVacancyResponseDTO,
    VacancyListItem as ApiVacancyListItem,
} from '../../api/interfaces';
import { VacancyListItem, vacancyListActions } from '../slices/vacancyListSlice';

function* fetchVacancyList({ payload: params }: PayloadAction<GetVacancyListParams>) {
    try {
        yield put(appActions.setLoading(Loading.App));

        const { data, totalCount, pageCount, currentPage }: ApiResponse<ApiVacancyListItem[]> = yield call(
            vacancyApi.getVacancyList,
            params,
        );
        const convertedData: VacancyListItem[] = data.map((i) => {
            return {
                id: i.id.toString(),
                name: i.name,
                isActive: i.isActive,
                isDraft: i.isDraft,
                isUpgraded: i.isUpgraded,
                rateType: i.rateType,
                rateFrom: i.rateFrom ? i.rateFrom.toString() : '',
                rateTo: i.rateTo ? i.rateTo.toString() : '',
                currencyId: i.currencyId,
                updatedAt: new Date(timestampWithNoMillisecondsToDate(i.updatedAt)),
                responses: i.responses,
                recommendations: i.recommendations,
                views: i.views,
                city: i.city,
                expireAt: timestampWithNoMillisecondsToDate(i.expireAt),
                expireSoon: i.expireSoon
            };
        });
        yield put(
            vacancyListActions.setVacancyList({
                data: convertedData,
                totalCount: totalCount as number,
                pageCount: pageCount as number,
                currentPage: currentPage as number,
            }),
        );
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* closeVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));

        const isActive: boolean = yield select((state: any) =>
            state.vacancyList.vacancies.find((v: VacancyListItem) => v.id === vacancyId)?.isActive,
        );
        yield call(vacancyApi.closeVacancy, vacancyId);
        if (isActive) { 
            dispatchVacancyClosed();
        }
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Вакансия успешно закрыта'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
        yield put(vacancyListActions.setShouldReloadLastFilter(true));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* openVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));

        yield call(vacancyApi.openVacancy, vacancyId);
        dispatchVacancyPublished();
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Вакансия успешно опубликована'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
        yield put(vacancyListActions.setShouldReloadLastFilter(true));
    } catch (e) {
        if ((e as AxiosError).response?.status === 402) {
            yield put(vacancyListActions.setShowVacancyLimitModal(true));
        } else {
            yield call(baseErrorHandler, e);
        }
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* renewVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(vacancyApi.renewVacancy, vacancyId);
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Вакансия успешно обновлена'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
        yield put(vacancyListActions.setShouldReloadLastFilter(true));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* upgradeVacancy({ payload }: PayloadAction<{ vacancyId: string; packageId: string }>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        const {
            data: { orderId },
        }: ApiResponse<UpgradeVacancyResponseDTO> = yield call(vacancyApi.upgradeVacancy, payload.vacancyId, {
            package: payload.packageId,
        });
        window.location.href = `/order/view?id=${orderId}`;
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* vacancyListSaga() {
    yield takeEvery(vacancyListActions.fetchVacancyList, fetchVacancyList);
    yield takeEvery(vacancyListActions.closeVacancyRequest, closeVacancy);
    yield takeEvery(vacancyListActions.openVacancyRequest, openVacancy);
    yield takeEvery(vacancyListActions.renewVacancyRequest, renewVacancy);
    yield takeEvery(vacancyListActions.upgradeVacancyRequest, upgradeVacancy);
}
